/**
 * PageSection - Consistent section layout for CMS-style pages
 *
 * Provides uniform structure for entity management pages:
 * - Title with optional badge/count
 * - Icon with styled background
 * - Description text
 * - Action buttons
 * - Content area with configurable padding
 *
 * Variants:
 * - 'default': Standard section with subtle styling
 * - 'card': Elevated card with shadow and border
 * - 'flush': No padding, content fills container
 * - 'hero': Large featured section for page headers
 */

import { ReactNode, CSSProperties, useState } from 'react';
import { LucideIcon } from 'lucide-react';

export interface PageSectionProps {
  // Header
  title?: string;
  icon?: LucideIcon;
  iconColor?: string;
  count?: number;
  description?: string;

  // Actions
  actions?: ReactNode;

  // Content
  children: ReactNode;

  // Styling
  variant?: 'default' | 'card' | 'flush' | 'hero';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  className?: string;
  style?: CSSProperties;

  // Behavior
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}

const PADDING_SIZES = {
  none: 0,
  sm: 12,
  md: 20,
  lg: 28,
};

const VARIANT_STYLES: Record<
  string,
  {
    background: string;
    borderRadius: string;
    border: string;
    shadow: string;
    headerBorder?: string;
    headerBackground?: string;
  }
> = {
  default: {
    background: 'transparent',
    borderRadius: '0',
    border: 'none',
    shadow: 'none',
    headerBorder: '1px solid var(--gray-100, #f1f5f9)',
  },
  card: {
    background: 'white',
    borderRadius: 'var(--radius-lg, 12px)',
    border: '1px solid var(--gray-200, #e5e7eb)',
    shadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.03)',
    headerBorder: '1px solid var(--gray-100, #f1f5f9)',
    headerBackground: 'linear-gradient(180deg, var(--gray-50, #f8fafc) 0%, white 100%)',
  },
  flush: {
    background: 'transparent',
    borderRadius: '0',
    border: 'none',
    shadow: 'none',
  },
  hero: {
    background: 'linear-gradient(135deg, var(--primary-50, #eff6ff) 0%, white 100%)',
    borderRadius: 'var(--radius-xl, 16px)',
    border: '1px solid var(--primary-100, #dbeafe)',
    shadow: '0 4px 12px rgba(59, 130, 246, 0.08)',
  },
};

export function PageSection({
  title,
  icon: Icon,
  iconColor,
  count,
  description,
  actions,
  children,
  variant = 'default',
  padding = 'md',
  className,
  style,
  collapsible = false,
  defaultCollapsed = false,
}: PageSectionProps) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const hasHeader = title || description || actions;
  const paddingValue = PADDING_SIZES[padding];
  const variantStyle = VARIANT_STYLES[variant] || VARIANT_STYLES.default;

  const wrapperStyles: CSSProperties = {
    background: variantStyle.background,
    borderRadius: variantStyle.borderRadius,
    border: variantStyle.border,
    boxShadow: variantStyle.shadow,
    overflow: 'hidden',
    transition: 'box-shadow 0.2s ease',
    ...style,
  };

  const headerStyles: CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '16px',
    padding: variant === 'hero' ? `${paddingValue + 8}px ${paddingValue}px` : paddingValue,
    paddingBottom: description ? 12 : paddingValue,
    borderBottom: variantStyle.headerBorder,
    background: variantStyle.headerBackground,
    cursor: collapsible ? 'pointer' : undefined,
  };

  const contentStyles: CSSProperties = {
    padding: variant === 'flush' ? 0 : paddingValue,
    display: isCollapsed ? 'none' : 'block',
  };

  // Determine icon background color
  const getIconBackground = () => {
    if (iconColor) {
      return `${iconColor}15`;
    }
    if (variant === 'hero') {
      return 'white';
    }
    return 'var(--primary-50, #eff6ff)';
  };

  const getIconColor = () => {
    if (iconColor) return iconColor;
    return 'var(--accent, #3b82f6)';
  };

  return (
    <div className={className} style={wrapperStyles}>
      {hasHeader && (
        <div
          style={headerStyles}
          onClick={collapsible ? () => setIsCollapsed(!isCollapsed) : undefined}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            {title && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {Icon && (
                  <div
                    style={{
                      width: variant === 'hero' ? 44 : 36,
                      height: variant === 'hero' ? 44 : 36,
                      borderRadius: 'var(--radius-md, 8px)',
                      background: getIconBackground(),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow:
                        variant === 'hero'
                          ? '0 2px 8px rgba(59, 130, 246, 0.15)'
                          : '0 1px 2px rgba(0,0,0,0.05)',
                      flexShrink: 0,
                    }}
                  >
                    <Icon
                      size={variant === 'hero' ? 22 : 18}
                      style={{ color: getIconColor() }}
                    />
                  </div>
                )}
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <h2
                      style={{
                        margin: 0,
                        fontSize: variant === 'hero' ? '22px' : '18px',
                        fontWeight: 600,
                        color: 'var(--gray-900, #1e293b)',
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {title}
                    </h2>
                    {count !== undefined && (
                      <span
                        style={{
                          padding: '3px 10px',
                          fontSize: '12px',
                          fontWeight: 600,
                          color: 'var(--gray-600, #475569)',
                          background: 'var(--gray-100, #f1f5f9)',
                          borderRadius: '20px',
                          letterSpacing: '0.02em',
                        }}
                      >
                        {count.toLocaleString()}
                      </span>
                    )}
                    {collapsible && (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{
                          color: 'var(--gray-400)',
                          transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s ease',
                        }}
                      >
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    )}
                  </div>
                  {description && (
                    <p
                      style={{
                        margin: '4px 0 0',
                        fontSize: '14px',
                        color: 'var(--gray-500, #64748b)',
                        lineHeight: 1.5,
                      }}
                    >
                      {description}
                    </p>
                  )}
                </div>
              </div>
            )}
            {!title && description && (
              <p
                style={{
                  margin: 0,
                  fontSize: '14px',
                  color: 'var(--gray-500, #64748b)',
                  lineHeight: 1.5,
                }}
              >
                {description}
              </p>
            )}
          </div>

          {actions && (
            <div style={{ display: 'flex', gap: '8px', flexShrink: 0, alignItems: 'center' }}>
              {actions}
            </div>
          )}
        </div>
      )}

      <div style={contentStyles}>{children}</div>
    </div>
  );
}

/**
 * PageHeader - Top-level page header with title, description, and actions
 */
export interface PageHeaderProps {
  title: string;
  subtitle?: string;
  description?: string;
  actions?: ReactNode;
  breadcrumb?: ReactNode;
  icon?: LucideIcon;
  iconColor?: string;
}

export function PageHeader({
  title,
  subtitle,
  description,
  actions,
  breadcrumb,
  icon: Icon,
  iconColor,
}: PageHeaderProps) {
  return (
    <div
      style={{
        marginBottom: '20px',
      }}
    >
      {breadcrumb && (
        <div
          style={{
            marginBottom: '8px',
            fontSize: '13px',
            color: 'var(--gray-500)',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          {breadcrumb}
        </div>
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
          {Icon && (
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 'var(--radius-md, 8px)',
                background: iconColor ? `${iconColor}15` : 'var(--accent-bg-medium, rgba(123, 75, 216, 0.1))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Icon
                size={18}
                style={{ color: iconColor || 'var(--accent, #7B4BD8)' }}
              />
            </div>
          )}
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
              <h1
                style={{
                  margin: 0,
                  fontSize: '20px',
                  fontWeight: 600,
                  color: 'var(--gray-900, #1e293b)',
                  letterSpacing: '-0.01em',
                  lineHeight: 1.3,
                }}
              >
                {title}
              </h1>
              {subtitle && (
                <span
                  style={{
                    fontSize: '13px',
                    fontWeight: 500,
                    color: 'var(--gray-400, #9ca3af)',
                  }}
                >
                  {subtitle}
                </span>
              )}
            </div>
            {description && (
              <p
                style={{
                  margin: '2px 0 0',
                  fontSize: '13px',
                  color: 'var(--gray-500, #64748b)',
                  lineHeight: 1.4,
                }}
              >
                {description}
              </p>
            )}
          </div>
        </div>
        {actions && (
          <div style={{ display: 'flex', gap: '8px', flexShrink: 0, alignItems: 'center' }}>
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}

export default PageSection;
